import { Outlet, useNavigate } from "react-router-dom";
import NavHomeDashboardAdminPartial from "../home/home/nav-home-dashboard.admin.partials";
import {
  type IFavoriteSocial,
  useGetAllFavoritesAdminHttp,
} from "../../../hooks/http/admin/favorites/get-all-favorites.admin.http";
import { debounce, handleHttpError } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";
import { useCallback, useEffect, useState } from "react";
import Spinner from "../../../components/spinner.components";
import { FavoriteCard } from "../../../components/favorite-card.components";
import type { IPagination } from "../../../types/models/pagination.models.types";
import PaginationComponent from "../../../components/pagination.components";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import { Input } from "../../../components/ui/input";
import { XIcon } from "lucide-react";
import { useFavoriteSocialSearchAdminHttp } from "../../../hooks/http/admin/favorites/favorite-social-search.admin.http";

export default function FavouritesAdminPartial() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [favorites, setFavorites] = useState<IFavoriteSocial[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [paginationData, setPaginationData] = useState<IPagination>();
  const [search, setSearch] = useState("");
  const [typing, setTyping] = useState(false);

  const { execute: getAllFavoritesHttp, fetching: fetchingFavorites } =
    useGetAllFavoritesAdminHttp();

  const {
    execute: searchFavoriteSocialsHttp,
    fetching: fetchingFavoriteSocials,
    cancel: cancelSearchFavoriteSocialHttp,
  } = useFavoriteSocialSearchAdminHttp();

  async function getAllFavorites(page: number) {
    const response = await getAllFavoritesHttp({
      body: null,
      queryParams: {
        size: 12,
        page,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }

    setFavorites((prevData) => {
      const newFavorites = response.data.favouriteSocials.filter((favorite) => {
        return prevData.findIndex((i) => i.id === favorite.id) === -1;
      });
      return [...prevData, ...newFavorites];
    });
    setPaginationData(response.data.pagination);
  }

  function newFavouriteGroup() {
    navigate("/admin/favorites/new");
  }

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }
      const response = await searchFavoriteSocialsHttp({
        body: null,
        queryParams: {
          searchString: searchString,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      setFavorites(response.data.favouriteSocials);
      setTyping(false);
    }, 500),
    []
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    cancelSearchFavoriteSocialHttp();
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
    setTyping(true);
    if (e.target.value === "") {
      setFavorites([]);
      document.getElementById("getSocials")?.click();
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (search.trim().length === 0) {
      getAllFavorites(pageNo);
    }
    if (search.trim().length) {
      setFavorites([]);
      setPaginationData(undefined);
    }
  }, [pageNo, search]);

  return (
    <div className="flex flex-col gap-8 px-8 pt-16 w-full h-full">
      <div className="flex justify-between items-center gap-8 w-full">
        <NavHomeDashboardAdminPartial
          buttonName="new favourite group"
          onClickFunction={newFavouriteGroup}
        />

        <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
          <SearchIcon />
          <Input
            value={search}
            onChange={(e) => {
              handleSearch(e);
            }}
            className="bg-transparent border-none w-80 font-semibold text-base placeholder:text-secondary"
            placeholder="Search by name, or description"
          />

          {search.trim().length > 0 && (
            <XIcon
              onClick={() => {
                setSearch("");
                setFavorites([]);
              }}
              className="right-5 absolute cursor-pointer"
            />
          )}
        </div>
      </div>
      {search.trim().length > 0 ? (
        fetchingFavoriteSocials || typing ? (
          <div className="flex justify-center items-center pt-36 w-full h-96">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col w-full">
            <div className="gap-11 grid grid-cols-3">
              {favorites.map((favorite) => (
                <FavoriteCard key={favorite.id} favorite={favorite} />
              ))}
            </div>
            {favorites.length === 0 && (
              <div className="flex flex-col justify-center items-center pt-36 w-full h-96">
                No favorite found
              </div>
            )}
          </div>
        )
      ) : (
        <div className="flex flex-col py-5">
          {favorites.length === 0 && fetchingFavorites && (
            <div className="flex justify-center items-center pt-36 w-full h-96">
              <Spinner />
            </div>
          )}
          {!fetchingFavorites && favorites.length === 0 && (
            <img
              className="w-full"
              src="/static/empty-favourites-state.png"
              alt=""
            />
          )}
          {favorites.length > 0 && (
            <div className="gap-11 grid grid-cols-3">
              {favorites.map((favorite) => (
                <FavoriteCard key={favorite.id} favorite={favorite} />
              ))}
            </div>
          )}
          {paginationData && (
            <PaginationComponent
              fetching={fetchingFavorites}
              paginationData={paginationData}
              onClick={() => {
                setPageNo(pageNo + 1);
              }}
            />
          )}
        </div>
      )}

      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        id="getAllFavorites"
        onClick={() => {
          setFavorites([]);
          getAllFavorites(1);
        }}
        className="hidden"
      />

      <Outlet />
    </div>
  );
}
